import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Compass } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { guestConciergeApi } from '../../services/supportChatService';
import ChatWidgetShell from './ChatWidgetShell';
import {
  createChatMessage,
  buildChatHistory,
  loadStoredChat,
  persistChat,
} from '../../utils/chatWidgetUtils';

const STORAGE_KEY = 'guest-concierge-chat';

const WELCOME =
  "Welcome! I'm your booking concierge. Tell me about your event—type, city, date, and budget—and I'll recommend services and help you book.";

const QUICK_PROMPTS = [
  'Photo booth for a wedding',
  'Corporate event entertainment',
  'How does booking work?',
  'Browse vendors near me',
];

const GuestConciergeWidget = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [assistantLabel, setAssistantLabel] = useState('Booking Concierge');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [statusChecked, setStatusChecked] = useState(false);
  const [hints, setHints] = useState({});

  const abortRef = useRef(null);

  useEffect(() => {
    if (authLoading || isAuthenticated) return;

    let cancelled = false;

    const init = async () => {
      let enabled = true;
      let label = 'Booking Concierge';
      try {
        const res = await guestConciergeApi.getStatus();
        enabled = Boolean(res?.data?.enabled);
        label = res?.data?.assistant_name || label;
      } catch {
        enabled = false;
      }
      if (cancelled) return;
      setChatEnabled(enabled);
      setAssistantLabel(label);
      setMessages(loadStoredChat(STORAGE_KEY, WELCOME));
      setStatusChecked(true);
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      persistChat(STORAGE_KEY, messages);
    }
  }, [messages, isAuthenticated]);

  useEffect(() => {
    const open = () => {
      setIsOpen(true);
      setIsMinimized(false);
    };
    window.addEventListener('openGuestConcierge', open);
    window.addEventListener('openChatWidget', open);
    return () => {
      window.removeEventListener('openGuestConcierge', open);
      window.removeEventListener('openChatWidget', open);
    };
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  const inferHints = useCallback((text, prev) => {
    const lower = text.toLowerCase();
    const next = { ...prev };
    if (/wedding/i.test(lower)) next.event_type = 'wedding';
    if (/corporate|company|office/i.test(lower)) next.event_type = 'corporate';
    if (/birthday/i.test(lower)) next.event_type = 'birthday';
    if (/photo\s*booth|photobooth/i.test(lower)) next.service_type = 'photo booth';
    if (/dj\b|disc jockey/i.test(lower)) next.service_type = 'dj';
    if (/planner|coordinator/i.test(lower)) next.service_type = 'planner';
    const budget = lower.match(/\$?\s*(\d{3,5})/);
    if (budget) next.max_budget = Number(budget[1]);
    return next;
  }, []);

  const sendToConcierge = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isSending) return;

    if (!chatEnabled) {
      setError('AI concierge is not configured. Add GEMINI_API_KEY to the API server.');
      return;
    }

    const nextHints = inferHints(trimmed, hints);
    setHints(nextHints);
    setError(null);
    setMessages((prev) => [...prev, createChatMessage(trimmed, 'user')]);
    setInputValue('');
    setIsSending(true);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await guestConciergeApi.sendMessage({
        message: trimmed,
        history: buildChatHistory(messages),
        hints: nextHints,
        signal: controller.signal,
      });

      const reply = res?.data?.reply?.trim();
      if (!reply) throw new Error('Empty response');

      setMessages((prev) => [
        ...prev,
        createChatMessage(reply, 'bot', {
          actions: res?.data?.actions || [],
          suggestions: res?.data?.suggestions || [],
        }),
      ]);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      setError(err?.response?.data?.message || err?.message || 'Something went wrong.');
      setMessages((prev) => [
        ...prev,
        createChatMessage(
          "I couldn't find recommendations right now. Try /browse-vendors or register at /register to continue.",
          'bot',
          {
            isError: true,
            actions: [{ label: 'Browse vendors', path: '/browse-vendors', type: 'link' }],
          }
        ),
      ]);
    } finally {
      setIsSending(false);
      abortRef.current = null;
    }
  };

  const handleClear = () => {
    abortRef.current?.abort();
    setIsSending(false);
    setError(null);
    setHints({});
    setMessages([createChatMessage(WELCOME, 'bot', { isWelcome: true })]);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  if (authLoading || isAuthenticated || !statusChecked) {
    return null;
  }

  return (
    <ChatWidgetShell
      variant="concierge"
      isOpen={isOpen}
      isMinimized={isMinimized}
      chatEnabled={chatEnabled}
      assistantLabel={assistantLabel}
      subtitle={chatEnabled ? 'Find & book the perfect service' : 'Offline'}
      messages={messages}
      inputValue={inputValue}
      onInputChange={setInputValue}
      onSend={sendToConcierge}
      isSending={isSending}
      error={error}
      onToggle={() => {
        if (isOpen && !isMinimized) setIsMinimized(true);
        else if (isOpen && isMinimized) setIsMinimized(false);
        else {
          setIsOpen(true);
          setIsMinimized(false);
        }
      }}
      onClose={() => {
        abortRef.current?.abort();
        setIsOpen(false);
        setIsMinimized(false);
      }}
      onClear={handleClear}
      quickPrompts={QUICK_PROMPTS}
      onQuickPrompt={sendToConcierge}
      openButtonLabel="Open booking concierge"
      fabIcon={Compass}
      fabClassName="bg-violet-900"
    />
  );
};

export default GuestConciergeWidget;
